import ConfirmationDialog from "@hudson/ui/components/Dialogs/Confirmation";
import MinorButton from "@hudson/ui/components/FormFields/ButtonMinor";
import destIcon from "@icons/destination.svg";
import useContent from "@lib/use_content";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { useState } from "react";

export default function Location({
  loc,
  onRemove,
  onEdit,
  submitting,
}: {
  loc: be.Location;
  onRemove: (id: string) => void;
  onEdit: (id: string) => void;
  submitting?: boolean;
}): JSX.Element {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { data, loading, error } = useContent([
    "beProfileLocationsEditButton",
    "beProfileLocationsRemoveButton",
    "beProfileLocationsRemoveDialogCancelButton",
    "beProfileLocationsRemoveDialogTitle",
    "beProfileLocationsRemoveDialogConfirmMessage",
    "beProfileLocationsRemoveDialogConfirmButton",
  ]);

  return (
    <>
      <div className={`flex flex-col w-full justify-between relative truncate`}>
        <div
          className={`flex-1 truncate pt-4 px-4 ${loc.latLng ? "absolute bg-white/60 z-50 rounded-tl-md pb-5 backdrop-blur-lg" : "pb-3 bg-white"}`}
          style={{
            "--mask": `linear-gradient(to top,
      rgba(0,0,0, 0) 0,  rgba(0,0,0, 1) 10%, rgba(0,0,0, 1) 0
  ) 100% 50% / 100% 100% repeat-x`,
            WebkitMask: "var(--mask)",
            mask: "var(--mask)",
          }}
        >
          <div className="flex items-center space-x-3">
            <h3 className="truncate text-lg font-semibold text-[--primary-color]">
              {loc.displayName}
            </h3>
          </div>
          <p className="mt-0.5 truncate text-sm text-gray-500">{loc.displayAddress}</p>
        </div>

        {loc.latLng && (
          <div className="relative overflow-hidden rounded-t-md">
            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "10rem" }}
              center={loc.latLng}
              onLoad={(map) => {
                map.setCenter(loc.latLng);
                map.setZoom(14);
              }}
              options={{
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false,
              }}
            >
              <Marker
                position={loc.latLng}
                icon={{ scaledSize: new google.maps.Size(35, 35), url: destIcon.src }}
              />
            </GoogleMap>
          </div>
        )}
      </div>
      <div>
        <div className="flex justify-end w-full px-4 py-2 space-x-2">
          <MinorButton
            disabled={submitting}
            loading={submitting}
            onClick={() => setShowDeleteConfirm(true)}
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="var(--primary-color)"
                className="w-5 h-5 text-gray-400"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                />
              </svg>
            }
            padding="px-3 py-1"
            noBorder
          >
            {data?.content.beProfileLocationsRemoveButton}
          </MinorButton>
          <MinorButton
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="size-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
                />
              </svg>
            }
            disabled={submitting}
            loading={submitting}
            padding="px-3 py-1"
            onClick={() => onEdit(loc.id)}
            noBorder
          >
            {data?.content.beProfileLocationsEditButton}
          </MinorButton>
        </div>

        <ConfirmationDialog
          cancelLabel={data?.content.beProfileLocationsRemoveDialogCancelButton}
          confirmLabel={data?.content.beProfileLocationsRemoveDialogConfirmButton}
          message={data?.content.beProfileLocationsRemoveDialogConfirmMessage}
          onConfirm={() => onRemove(loc.id)}
          onHide={() => setShowDeleteConfirm(false)}
          show={showDeleteConfirm}
          title={data?.content.beProfileLocationsRemoveDialogTitle}
        />
      </div>
    </>
  );
}
